/**
 * Theme bridge: turn the live CSS tokens (Bootstrap + our --genus-* vars) into
 * an ApexCharts option layer, so every chart looks native in both light and
 * dark themes and can be re-themed on the fly when the app theme flips.
 *
 * The returned options are merged UNDER the page's own config (the page wins),
 * and recomputed + re-applied whenever `data-bs-theme` changes on <html>.
 */

// Series palette, read from the theme colors first (so per-app distinctiveness
// comes from the SCSS variables, like the rest of the template), then extended
// with a few fixed hues for charts that have many series. Empty vars are
// dropped, so the palette is always as long as the theme actually defines.
const PALETTE_VARS = [
	'--bs-primary', '--bs-success', '--bs-info', '--bs-warning', '--bs-danger',
	'--bs-purple', '--bs-teal', '--bs-pink', '--bs-cyan', '--bs-orange', '--bs-indigo',
];

const read = (styles, name, fallback) => (styles.getPropertyValue(name) || '').trim() || fallback;

export const isDark = () => document.documentElement.getAttribute('data-bs-theme') === 'dark';

// A translated interface string (respects g.lang), with a plain fallback.
const t = (text) => (window.g && window.g.translate ? window.g.translate(text) : text);

// Snapshot the theme tokens that a chart needs.
export function readTokens() {
	const s = getComputedStyle(document.documentElement);
	const dark = isDark();
	return {
		dark,
		palette: PALETTE_VARS.map((v) => read(s, v, '')).filter(Boolean),
		font: read(s, '--bs-body-font-family', 'inherit'),
		text: read(s, '--bs-body-color', dark ? '#aab1c0' : '#212529'),
		muted: read(s, '--bs-secondary-color', dark ? 'rgba(255,255,255,.5)' : 'rgba(0,0,0,.5)'),
		border: read(s, '--bs-border-color', dark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.1)'),
		card: read(s, '--genus-card-bg', dark ? '#272c39' : '#ffffff'),
	};
}

// Pie-family charts draw a gap stroke between slices; it must be the card
// surface, not a fixed white, so it stays right in dark mode and on switch.
const PIE_FAMILY = ['pie', 'donut', 'radialBar', 'polarArea'];

/**
 * The theme option layer. `reduceMotion` disables the entry animation (honors
 * prefers-reduced-motion). Everything here is a sensible default a page may
 * override; nothing here carries data.
 */
export function themeOptions(reduceMotion, type) {
	const c = readTokens();
	const pie = PIE_FAMILY.indexOf(type) !== -1;
	return {
		chart: {
			fontFamily: c.font,
			foreColor: c.text,
			background: 'transparent',
			toolbar: { show: false },
			animations: { enabled: !reduceMotion, speed: 400 },
		},
		colors: c.palette,
		grid: {
			borderColor: c.border,
			strokeDashArray: 4,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
			padding: { top: 0, right: 8, bottom: 0, left: 8 },
		},
		dataLabels: { enabled: false },
		stroke: pie ? { colors: [c.card], width: 2 } : { curve: 'smooth', width: 2.5, lineCap: 'round' },
		markers: { strokeColors: 'transparent', hover: { sizeOffset: 2 } },
		legend: {
			position: 'top',
			horizontalAlign: 'left',
			fontFamily: c.font,
			fontWeight: 500,
			markers: { radius: 12, offsetX: -2 },
			itemMargin: { horizontal: 10, vertical: 4 },
			labels: { colors: c.text },
		},
		tooltip: { theme: c.dark ? 'dark' : 'light', style: { fontFamily: c.font } },
		xaxis: {
			labels: { style: { colors: c.muted, fontFamily: c.font } },
			axisBorder: { color: c.border },
			axisTicks: { color: c.border },
		},
		yaxis: { labels: { style: { colors: c.muted, fontFamily: c.font } } },
		noData: { text: t('No data'), style: { color: c.muted, fontFamily: c.font } },
	};
}
